"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { RatingStar } from "@/components/icon/IconList";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { Badge, Button, Icon, Tag, Thumbnail, Typography } from "@bookips/sds";
import Link from "next/link";
import { useRouter } from "next/router";
import { getUsableEntitlement } from "../../utils/entitlement";
/** 이벤트 버블링 방지 핸들러 */ const stopPropagation = (e)=>{
    e.stopPropagation();
};
/** step 코드 → 텍스트 변환 */ export const stepCodeLookup = {
    10: "step1. 개념완전정복",
    20: "step2. 최다빈출 대표유형",
    30: "step3. 최신 기출변형",
    40: "step4. 초고난도 기출변형",
    50: "step5. 실전 모의고사"
};
/**
 * unit, step, category.tag에서 태그 배열 생성
 */ const createTags = (unit, step, categoryTag)=>[
        ...unit ? [
            {
                label: unit,
                color: "indigo",
                bold: true
            }
        ] : [],
        ...step && stepCodeLookup[step] ? [
            {
                label: stepCodeLookup[step],
                color: "gray"
            }
        ] : [],
        ...categoryTag ? [
            {
                label: categoryTag,
                color: "gray"
            }
        ] : []
    ];
/**
 * 상품 데이터에서 타입 추론 (freeTrial.reason 기반)
 *
 * reason에 따른 분기:
 * - eligible: true → freeTrial (무료 체험 가능)
 * - not_logged_in → freeTrial (비로그인이지만 무료 체험 가능)
 * - already_owned → owned (이미 소유중)
 * - daily_limit_exceeded, excluded_membership → trialOver (무료 사용 한도 초과 또는 제외 이용권)
 * - not_target_user → excluded (유저타입 미대상)
 *
 * NOTE: excluded_membership 케이스는 이용 수단(getUsableEntitlement)과 purchaseOptions로 별도 처리
 */ export const getProductType = (product)=>{
    const { freeTrial  } = product;
    // freeTrial 정보가 없으면 기본 타입 (freepass 포함)
    if (!freeTrial) {
        return product.handoutType === "freepass" ? "freepass" : "basic";
    }
    // eligible이 true이면 무료 체험 가능
    if (freeTrial.eligible === true) return "freeTrial";
    // reason에 따른 분기
    switch(freeTrial.reason){
        case "not_logged_in":
            return "freeTrial" // 비로그인 상태지만 무료 체험 가능
            ;
        case "already_owned":
            return "owned" // 이미 소유중
            ;
        case "daily_limit_exceeded":
        case "excluded_membership":
            // freepass 상품은 무료체험 한도와 무관하게 패스 구매 페이지로 이동 가능
            return product.handoutType === "freepass" ? "freepass" : "trialOver";
        case "not_target_user":
            // freepass 상품이면서 대상 유저가 아닌 경우 → 무제한 이용하기 버튼 표시
            return product.handoutType === "freepass" ? "freepass" : "excluded";
        default:
            break;
    }
    return "basic";
};
/** 가격/상태 텍스트 */ const PriceArea = (param)=>{
    let { type , price , rating , showCouponTag , tagSize ="sm" , isBold =false , hasUsableEntitlement =false  } = param;
    // 타입별 가격 영역 텍스트
    const getPriceText = ()=>{
        // 이용권이 있고(이 상품에 해당하는 이용권은 아님) 아직 획득 전이면 서버에서 price 문자열 그대로 사용 (예: "쏠북패스 이용시 0원")
        if (hasUsableEntitlement) {
            if (typeof price === "string") return price;
            if (typeof price === "number") return "".concat(price);
            return null;
        }
        switch(type){
            case "freeTrial":
                return "무료 체험 가능";
            case "trialOver":
            case "excluded":
            case "freepass":
                // 서버에서 오는 price 문자열 그대로 사용 (예: "쏠북패스 이용시 0원")
                return typeof price === "string" ? price : null;
            case "owned":
                return "보유중인 자료";
            case "basic":
            default:
                return price !== undefined && typeof price === "number" ? "".concat(price.toLocaleString(), "원") : null;
        }
    };
    const priceText = getPriceText();
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-1.5",
        children: [
            priceText && /*#__PURE__*/ _jsx(Typography, {
                variant: "subtitle1",
                className: cn("text-primary", isBold && "font-bold"),
                children: priceText
            }),
            showCouponTag && /*#__PURE__*/ _jsx(Tag, {
                color: "red",
                size: tagSize,
                bold: true,
                children: "쿠폰"
            }),
            rating !== undefined && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-0.5",
                children: [
                    /*#__PURE__*/ _jsx(RatingStar, {}),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "caption",
                        className: "text-primary",
                        children: rating.toFixed(1)
                    })
                ]
            })
        ]
    });
};
/**
 * 타입별 버튼 설정 가져오기
 *
 * 버튼 분기:
 * - hasUsableEntitlement이 true: 이용권 보유 시 열람/출력 버튼 (owned처럼 동작)
 * - freeTrial (not_logged_in 포함): 샘플 보기, 무료로 받기
 * - owned (already_owned): licensePlan에 따라 다름
 *   - download: Desktop(열람 및 출력, PDF 다운) / 나머지(뷰어로 열람, PDF 다운)
 *   - view: Desktop(열람 및 출력) / 나머지(뷰어로 열람)
 * - trialOver (daily_limit_exceeded, excluded_membership): 샘플 보기, 무제한 이용하기
 * - excluded (not_target_user): 샘플 보기, 무제한 이용하기
 * - basic (freeTrial이 null인 경우): licensePlan에 따라 다름
 *   - null: 샘플 보기만
 *   - view: Desktop(열람 및 출력) / 나머지(뷰어로 열람)
 *   - download: Desktop(열람 및 출력, PDF 다운) / 나머지(뷰어로 열람, PDF 다운)
 */ const getButtonsConfig = (type, plan, hasUsableEntitlement, onSampleClick, onCtaClick, onPdfDownloadClick)=>{
    // 쓸 수 있는 이용 수단이 있고 plan이 있으면 이용권 보유 시와 동일하게 버튼 표시
    if (hasUsableEntitlement && plan) {
        if (plan === "download") {
            return {
                primary: {
                    label: "뷰어로 열람",
                    desktopLabel: "열람 및 출력",
                    onClick: onCtaClick
                },
                secondary: {
                    label: "PDF 다운",
                    onClick: onPdfDownloadClick,
                    hasIcon: true
                }
            };
        }
        // view
        return {
            primary: {
                label: "뷰어로 열람",
                desktopLabel: "열람 및 출력",
                onClick: onCtaClick
            },
            secondary: null
        };
    }
    switch(type){
        case "freeTrial":
            // 비로그인 또는 무료 체험 가능: 샘플 보기 + 무료로 받기
            return {
                primary: {
                    label: "샘플 보기",
                    onClick: onSampleClick
                },
                secondary: {
                    label: "무료로 받기",
                    onClick: onCtaClick
                }
            };
        case "trialOver":
            // 무료 사용 한도 초과 또는 제외 이용권: 샘플 보기 + 무제한 이용하기
            return {
                primary: {
                    label: "샘플 보기",
                    onClick: onSampleClick
                },
                secondary: {
                    label: "무제한 이용하기",
                    onClick: onCtaClick
                }
            };
        case "owned":
            // 이미 소유중: licensePlan에 따라 버튼 구성
            // download: Desktop(열람 및 출력, PDF 다운) / 나머지(뷰어로 열람, PDF 다운)
            // view: Desktop(열람 및 출력) / 나머지(뷰어로 열람)
            if (plan === "download") {
                return {
                    primary: {
                        label: "뷰어로 열람",
                        desktopLabel: "열람 및 출력",
                        onClick: onCtaClick
                    },
                    secondary: {
                        label: "PDF 다운",
                        onClick: onPdfDownloadClick,
                        hasIcon: true
                    }
                };
            }
            // view (또는 plan 없는 경우 기본값)
            return {
                primary: {
                    label: "뷰어로 열람",
                    desktopLabel: "열람 및 출력",
                    onClick: onCtaClick
                },
                secondary: null
            };
        case "excluded":
            // 유저타입 미대상: 샘플 보기 + 무제한 이용하기
            return {
                primary: {
                    label: "샘플 보기",
                    onClick: onSampleClick
                },
                secondary: {
                    label: "무제한 이용하기",
                    onClick: onCtaClick
                }
            };
        case "freepass":
            // 프리패스 자료: 샘플 보기 + 무제한 이용하기
            return {
                primary: {
                    label: "샘플 보기",
                    onClick: onSampleClick
                },
                secondary: {
                    label: "무제한 이용하기",
                    onClick: onCtaClick
                }
            };
        case "basic":
        default:
            // freeTrial이 null인 경우: licensePlan에 따라 버튼 구성
            // licensePlan이 null이면 샘플 보기만
            // view이면 열람 및 출력
            // download이면 열람 및 출력 + PDF 다운
            if (plan === "download") {
                return {
                    primary: {
                        label: "뷰어로 열람",
                        desktopLabel: "열람 및 출력",
                        onClick: onCtaClick
                    },
                    secondary: {
                        label: "PDF 다운",
                        onClick: onPdfDownloadClick,
                        hasIcon: true
                    }
                };
            }
            if (plan === "view") {
                return {
                    primary: {
                        label: "뷰어로 열람",
                        desktopLabel: "열람 및 출력",
                        onClick: onCtaClick
                    },
                    secondary: null
                };
            }
            // licensePlan이 null인 경우: 샘플 보기만
            return {
                primary: null,
                secondary: {
                    label: "샘플 보기",
                    onClick: onSampleClick
                }
            };
    }
};
/** CTA 버튼 영역 - Desktop (lg 이상): 세로 배치 */ const CtaAreaDesktop = (param)=>{
    let { type , buttons , hasUsableEntitlement , onTrackClick  } = param;
    var _buttons_secondary;
    const handlePrimaryClick = (e)=>{
        var _buttons_primary, _buttons_primary1, _buttons_primary2, _buttons_primary_onClick;
        e.stopPropagation();
        onTrackClick === null || onTrackClick === void 0 ? void 0 : onTrackClick(((_buttons_primary = buttons.primary) === null || _buttons_primary === void 0 ? void 0 : _buttons_primary.desktopLabel) || ((_buttons_primary1 = buttons.primary) === null || _buttons_primary1 === void 0 ? void 0 : _buttons_primary1.label) || "");
        (_buttons_primary2 = buttons.primary) === null || _buttons_primary2 === void 0 ? void 0 : (_buttons_primary_onClick = _buttons_primary2.onClick) === null || _buttons_primary_onClick === void 0 ? void 0 : _buttons_primary_onClick.call(_buttons_primary2);
    };
    const handleSecondaryClick = (e)=>{
        var _buttons_secondary, _buttons_secondary1, _buttons_secondary2, _buttons_secondary_onClick;
        e.stopPropagation();
        onTrackClick === null || onTrackClick === void 0 ? void 0 : onTrackClick(((_buttons_secondary = buttons.secondary) === null || _buttons_secondary === void 0 ? void 0 : _buttons_secondary.desktopLabel) || ((_buttons_secondary1 = buttons.secondary) === null || _buttons_secondary1 === void 0 ? void 0 : _buttons_secondary1.label) || "");
        (_buttons_secondary2 = buttons.secondary) === null || _buttons_secondary2 === void 0 ? void 0 : (_buttons_secondary_onClick = _buttons_secondary2.onClick) === null || _buttons_secondary_onClick === void 0 ? void 0 : _buttons_secondary_onClick.call(_buttons_secondary2);
    };
    // hasUsableEntitlement이면 secondary 색상, 아니면 기존 로직
    const secondaryColor = hasUsableEntitlement || !(type === "freeTrial" || type === "trialOver" || ((_buttons_secondary = buttons.secondary) === null || _buttons_secondary === void 0 ? void 0 : _buttons_secondary.label) === "무제한 이용하기") ? "secondary" : "primary";
    return /*#__PURE__*/ _jsxs("div", {
        className: "hidden w-[114px] shrink-0 flex-col gap-1 self-center lg:flex",
        children: [
            buttons.primary && /*#__PURE__*/ _jsx(Button, {
                variant: "stroke",
                color: "secondary",
                size: "sm",
                onClick: handlePrimaryClick,
                children: buttons.primary.desktopLabel || buttons.primary.label
            }),
            buttons.secondary && /*#__PURE__*/ _jsx(Button, {
                variant: "stroke",
                color: secondaryColor,
                size: "sm",
                onClick: handleSecondaryClick,
                children: /*#__PURE__*/ _jsxs("span", {
                    className: "flex items-center justify-center gap-1",
                    children: [
                        buttons.secondary.hasIcon && /*#__PURE__*/ _jsx(Icon.Download_2px, {
                            className: "size-4"
                        }),
                        buttons.secondary.desktopLabel || buttons.secondary.label
                    ]
                })
            })
        ]
    });
};
/** CTA 버튼 영역 - Tablet (sm ~ lg): 가로 배치 */ const CtaAreaTablet = (param)=>{
    let { type , buttons , hasUsableEntitlement , onTrackClick  } = param;
    var _buttons_secondary;
    const handlePrimaryClick = (e)=>{
        var _buttons_primary, _buttons_primary1, _buttons_primary_onClick;
        e.stopPropagation();
        onTrackClick === null || onTrackClick === void 0 ? void 0 : onTrackClick(((_buttons_primary = buttons.primary) === null || _buttons_primary === void 0 ? void 0 : _buttons_primary.label) || "");
        (_buttons_primary1 = buttons.primary) === null || _buttons_primary1 === void 0 ? void 0 : (_buttons_primary_onClick = _buttons_primary1.onClick) === null || _buttons_primary_onClick === void 0 ? void 0 : _buttons_primary_onClick.call(_buttons_primary1);
    };
    const handleSecondaryClick = (e)=>{
        var _buttons_secondary, _buttons_secondary1, _buttons_secondary_onClick;
        e.stopPropagation();
        onTrackClick === null || onTrackClick === void 0 ? void 0 : onTrackClick(((_buttons_secondary = buttons.secondary) === null || _buttons_secondary === void 0 ? void 0 : _buttons_secondary.label) || "");
        (_buttons_secondary1 = buttons.secondary) === null || _buttons_secondary1 === void 0 ? void 0 : (_buttons_secondary_onClick = _buttons_secondary1.onClick) === null || _buttons_secondary_onClick === void 0 ? void 0 : _buttons_secondary_onClick.call(_buttons_secondary1);
    };
    // hasUsableEntitlement이면 secondary 색상, 아니면 기존 로직
    const secondaryColor = hasUsableEntitlement || !(type === "freeTrial" || type === "trialOver" || ((_buttons_secondary = buttons.secondary) === null || _buttons_secondary === void 0 ? void 0 : _buttons_secondary.label) === "무제한 이용하기") ? "secondary" : "primary";
    return /*#__PURE__*/ _jsxs("div", {
        className: "hidden shrink-0 gap-2 sm:flex lg:hidden",
        children: [
            buttons.primary && /*#__PURE__*/ _jsx(Button, {
                variant: "stroke",
                color: "secondary",
                size: "sm",
                onClick: handlePrimaryClick,
                children: buttons.primary.label
            }),
            buttons.secondary && /*#__PURE__*/ _jsx(Button, {
                variant: "stroke",
                color: secondaryColor,
                size: "sm",
                onClick: handleSecondaryClick,
                children: /*#__PURE__*/ _jsxs("span", {
                    className: "flex items-center justify-center gap-1",
                    children: [
                        buttons.secondary.hasIcon && /*#__PURE__*/ _jsx(Icon.Download_2px, {
                            className: "size-4"
                        }),
                        buttons.secondary.label
                    ]
                })
            })
        ]
    });
};
/** CTA 버튼 영역 - Mobile (sm 미만): 버튼 1개 또는 2개 */ const CtaAreaMobile = (param)=>{
    let { type , buttons , hasUsableEntitlement , onTrackClick  } = param;
    const handlePrimaryClick = (e)=>{
        var _buttons_primary, _buttons_primary1, _buttons_primary_onClick;
        e.stopPropagation();
        onTrackClick === null || onTrackClick === void 0 ? void 0 : onTrackClick(((_buttons_primary = buttons.primary) === null || _buttons_primary === void 0 ? void 0 : _buttons_primary.label) || "");
        (_buttons_primary1 = buttons.primary) === null || _buttons_primary1 === void 0 ? void 0 : (_buttons_primary_onClick = _buttons_primary1.onClick) === null || _buttons_primary_onClick === void 0 ? void 0 : _buttons_primary_onClick.call(_buttons_primary1);
    };
    const handleSecondaryClick = (e)=>{
        var _buttons_secondary, _buttons_secondary1, _buttons_secondary_onClick;
        e.stopPropagation();
        onTrackClick === null || onTrackClick === void 0 ? void 0 : onTrackClick(((_buttons_secondary = buttons.secondary) === null || _buttons_secondary === void 0 ? void 0 : _buttons_secondary.label) || "");
        (_buttons_secondary1 = buttons.secondary) === null || _buttons_secondary1 === void 0 ? void 0 : (_buttons_secondary_onClick = _buttons_secondary1.onClick) === null || _buttons_secondary_onClick === void 0 ? void 0 : _buttons_secondary_onClick.call(_buttons_secondary1);
    };
    // 두 버튼이 모두 있는 경우 (owned/excluded/basic 타입에서 licensePlan이 download인 경우)
    if (buttons.primary && buttons.secondary) {
        // sm 미만에서 샘플 보기 + 다른 버튼 조합일 경우 샘플 보기 숨기고 CTA 버튼만 표시
        if (buttons.primary.label === "샘플 보기") {
            return /*#__PURE__*/ _jsx(Button, {
                variant: "stroke",
                color: "primary",
                size: "sm",
                fullWidth: true,
                onClick: handleSecondaryClick,
                className: "mt-3 sm:hidden",
                children: buttons.secondary.label
            });
        }
        // 그 외 조합 (열람 및 출력 + PDF 다운 등)은 가로로 나란히 배치
        return /*#__PURE__*/ _jsxs("div", {
            className: "mt-3 flex w-full gap-2 sm:hidden",
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    variant: "stroke",
                    color: "secondary",
                    size: "sm",
                    fullWidth: true,
                    onClick: handlePrimaryClick,
                    children: buttons.primary.label
                }),
                /*#__PURE__*/ _jsx(Button, {
                    variant: "stroke",
                    color: "secondary",
                    size: "sm",
                    fullWidth: true,
                    onClick: handleSecondaryClick,
                    children: /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-center justify-center gap-1",
                        children: [
                            buttons.secondary.hasIcon && /*#__PURE__*/ _jsx(Icon.Download_2px, {
                                className: "size-4"
                            }),
                            buttons.secondary.label
                        ]
                    })
                })
            ]
        });
    }
    // 한 개의 버튼만 있는 경우
    // hasUsableEntitlement이 true면 owned처럼 동작 (primary 우선)
    // freeTrial, trialOver, excluded 타입은 secondary(CTA 버튼)를 우선, 나머지는 primary 우선
    const mobileButton = hasUsableEntitlement || type !== "freeTrial" && type !== "trialOver" && type !== "excluded" && type !== "freepass" ? buttons.primary || buttons.secondary : buttons.secondary || buttons.primary;
    // 샘플 보기 버튼만 있는 경우 모바일에서 비노출
    if (!mobileButton || mobileButton.label === "샘플 보기") return null;
    // freeTrial, trialOver 타입이면서 hasUsableEntitlement이 없을 때만 primary variant 사용
    // 또는 무제한 이용하기 버튼일 때 primary variant 사용
    const isPrimaryVariant = !hasUsableEntitlement && (type === "freeTrial" && buttons.secondary || type === "trialOver" || (mobileButton === null || mobileButton === void 0 ? void 0 : mobileButton.label) === "무제한 이용하기");
    const handleMobileClick = (e)=>{
        var _mobileButton_onClick;
        e.stopPropagation();
        onTrackClick === null || onTrackClick === void 0 ? void 0 : onTrackClick(mobileButton.label || "");
        (_mobileButton_onClick = mobileButton.onClick) === null || _mobileButton_onClick === void 0 ? void 0 : _mobileButton_onClick.call(mobileButton);
    };
    return /*#__PURE__*/ _jsx(Button, {
        variant: "stroke",
        color: isPrimaryVariant ? "primary" : "secondary",
        size: "sm",
        fullWidth: true,
        onClick: handleMobileClick,
        className: "mt-3 sm:hidden",
        children: mobileButton.label
    });
};
/**
 * 상품 리스트 아이템 컴포넌트
 * - 반응형 레이아웃 (Desktop/Tablet/Mobile)
 * - 타입별 CTA 버튼 및 가격 영역 변경
 * - @bookips/sds Thumbnail 컴포넌트 사용
 */ export const ProductListItem = (param)=>{
    let { className , product , href , onSampleClick , onCtaClick , onPdfDownloadClick , pageType ="edition" , acquiredLicenseInfo  } = param;
    var _buttons_secondary;
    const router = useRouter();
    // product에서 필요한 데이터 추출
    const { id , imageUrl , title , author , authorId , authorBrandUrl , handoutType , sourceName , price , rating , isCouponAvailable , unit , category , freeTrial , licenseId , licensePlan , purchaseOptions , step , subject , sourceId  } = product;
    // unit, step, category.tag에서 태그 생성
    const tags = createTags(unit, step, category === null || category === void 0 ? void 0 : category.tag);
    // 상품 타입 자동 추론
    const baseType = getProductType(product);
    // 라이센스 보유 여부 (API 응답의 licenseId가 있는 경우)
    const hasLicense = !!(licenseId && licensePlan);
    // 낙관적 업데이트: 획득한 라이센스가 있으면 'owned' 타입으로 처리
    const isAcquired = !!acquiredLicenseInfo;
    // 라이센스 보유 시 'owned' 타입으로 처리 (freepass 포함)
    const type = isAcquired || hasLicense ? "owned" : baseType;
    // 가격 숫자로 변환 (빈 문자열이나 파싱 불가한 값은 undefined 처리)
    // freepass, excluded 타입은 서버에서 오는 문자열 그대로 사용 (예: "22개정 패스 이용시 0원")
    const priceForDisplay = (()=>{
        if ((baseType === "freepass" || baseType === "excluded") && typeof price === "string") {
            return price;
        }
        if (price === undefined || price === null || price === "") return undefined;
        const parsed = typeof price === "string" ? parseInt(price, 10) : price;
        return Number.isNaN(parsed) ? undefined : parsed;
    })();
    const productHref = href || "/products/".concat(id);
    // 버튼 설정
    // 우선순위:
    // 1. 낙관적 업데이트로 획득한 라이센스가 있으면: acquiredLicenseInfo.licensePlan 기준
    // 2. licenseId가 있으면 (이미 자료 소유): licensePlan 기준으로 버튼 노출
    // 3. 이용 수단(이용권/구독) + purchaseOptions가 있으면: purchaseOptions 기준
    // 4. 그 외: licensePlan 기준
    const hasUsableEntitlement = !hasLicense && !isAcquired && !!(getUsableEntitlement(product) && (purchaseOptions === null || purchaseOptions === void 0 ? void 0 : purchaseOptions.length));
    const getPlanForButtons = ()=>{
        // 1. 낙관적 업데이트로 획득한 경우
        if (isAcquired) {
            return acquiredLicenseInfo.licensePlan;
        }
        // 2. licenseId가 있으면 licensePlan 사용
        if (hasLicense) {
            return licensePlan;
        }
        // 3. 쓸 수 있는 이용 수단이 있으면 purchaseOptions 기반
        if (hasUsableEntitlement) {
            return purchaseOptions.includes("download") ? "download" : "view";
        }
        // 4. 그 외: licensePlan 사용
        return licensePlan;
    };
    const planForButtons = getPlanForButtons();
    const buttons = getButtonsConfig(type, planForButtons, hasUsableEntitlement, onSampleClick, onCtaClick, onPdfDownloadClick);
    // 쿠폰 태그 표시 조건: isCouponAvailable이 true이고 샘플 보기 버튼만 존재하는 경우
    const isSampleButtonOnly = buttons.primary === null && ((_buttons_secondary = buttons.secondary) === null || _buttons_secondary === void 0 ? void 0 : _buttons_secondary.label) === "샘플 보기";
    const showCouponTag = isCouponAvailable && isSampleButtonOnly;
    // 페이지 타입별 Position 값
    const position = pageType === "system" ? "System Category" : "Edition Category Product List";
    // MX-362/MX-368: 전체 영역 클릭 시 상세 페이지로 이동 및 이벤트 트래킹
    const handleItemClick = ()=>{
        if (pageType === "system") {
            // MX-368: System Category용 상세 이벤트
            mixpanelEvent("Handout Product Clicked", {
                Position: position,
                "Free Trial": (freeTrial === null || freeTrial === void 0 ? void 0 : freeTrial.eligible) ? "true" : "false",
                "Author Name": author || "",
                "Author Id": authorId || "",
                Price: (priceForDisplay === null || priceForDisplay === void 0 ? void 0 : priceForDisplay.toString()) || "",
                "Product Id": String(id),
                "Product Title": title || "",
                "Product Type": "handout",
                "Product Subject": subject || "",
                "Handout Type": handoutType || "",
                "Handout Unit": unit || "",
                "Source Id": sourceId || "",
                "Source Name": sourceName || "",
                "Has Coupon Badge": isCouponAvailable ? "True" : "False",
                "Has Review Badge": rating !== undefined && rating !== null ? "True" : "False"
            });
        } else {
            // MX-362: Edition Category용 이벤트
            mixpanelEvent("Handout Product Clicked", {
                "Button URL": productHref,
                "Product ID": id,
                "Product Title": title,
                Position: position
            });
        }
        router.push(productHref);
    };
    // MX-361/MX-367: CTA 버튼 클릭 이벤트 트래킹
    const trackCtaButtonClick = (buttonName)=>{
        if (pageType === "system") {
            // MX-367: System Category용 이벤트
            mixpanelEvent("Product List CTA Button Clicked", {
                Position: position,
                "Product Id": String(id),
                "Product Title": title || "",
                "Product Author": author || "",
                "Button Name": buttonName
            });
        } else {
            // MX-361: Edition Category용 이벤트
            mixpanelEvent("Product List CTA Button Clicked", {
                "Button Name": buttonName,
                "Product ID": id,
                "Product Title": title,
                Position: position
            });
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("group/item cursor-pointer p-5", className),
        onClick: handleItemClick,
        role: "link",
        tabIndex: 0,
        onKeyDown: (e)=>{
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleItemClick();
            }
        },
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "hidden sm:flex sm:flex-col sm:gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "relative shrink-0",
                                children: [
                                    type === "owned" && /*#__PURE__*/ _jsx(Badge, {
                                        color: "indigo",
                                        label: "보유중",
                                        type: "text",
                                        className: "absolute top-[-10px] left-[-7px] px-1.5"
                                    }),
                                    /*#__PURE__*/ _jsx(Thumbnail, {
                                        size: "sm",
                                        src: imageUrl,
                                        alt: title,
                                        border: true,
                                        classes: {
                                            root: "shrink-0"
                                        }
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body1",
                                        className: "text-primary group-hover/item:text-primary-main group-has-[button:hover]/item:text-primary line-clamp-3",
                                        children: title
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-0.5",
                                        children: authorBrandUrl ? /*#__PURE__*/ _jsx(Link, {
                                            href: authorBrandUrl,
                                            className: "inline-block",
                                            onClick: stopPropagation,
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary cursor-pointer hover:underline",
                                                children: author
                                            })
                                        }) : /*#__PURE__*/ _jsx(Typography, {
                                            variant: "body2",
                                            className: "text-secondary",
                                            children: author
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "md:hidden",
                                                children: /*#__PURE__*/ _jsx(PriceArea, {
                                                    type: type,
                                                    price: priceForDisplay,
                                                    rating: rating !== null && rating !== void 0 ? rating : undefined,
                                                    showCouponTag: showCouponTag,
                                                    tagSize: "sm",
                                                    isBold: true,
                                                    hasUsableEntitlement: hasUsableEntitlement
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "hidden md:block",
                                                children: /*#__PURE__*/ _jsx(PriceArea, {
                                                    type: type,
                                                    price: priceForDisplay,
                                                    rating: rating !== null && rating !== void 0 ? rating : undefined,
                                                    showCouponTag: showCouponTag,
                                                    tagSize: "sm",
                                                    hasUsableEntitlement: hasUsableEntitlement
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }),
                            tags.length > 0 && /*#__PURE__*/ _jsx("div", {
                                className: "hidden w-[200px] shrink-0 flex-col items-start gap-1 self-center lg:flex",
                                children: tags.map((tag, index)=>/*#__PURE__*/ _jsx(Tag, {
                                        color: tag.color || "indigo",
                                        size: "md",
                                        bold: tag.bold,
                                        children: tag.label
                                    }, index))
                            }),
                            /*#__PURE__*/ _jsx(CtaAreaDesktop, {
                                type: type,
                                buttons: buttons,
                                hasUsableEntitlement: hasUsableEntitlement,
                                onTrackClick: trackCtaButtonClick
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "hidden items-center justify-between sm:flex lg:hidden",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex flex-1 flex-wrap gap-1",
                                children: tags.map((tag, index)=>/*#__PURE__*/ _jsx(Tag, {
                                        color: tag.color || "indigo",
                                        size: "md",
                                        bold: tag.bold,
                                        children: tag.label
                                    }, index))
                            }),
                            /*#__PURE__*/ _jsx(CtaAreaTablet, {
                                type: type,
                                buttons: buttons,
                                hasUsableEntitlement: hasUsableEntitlement,
                                onTrackClick: trackCtaButtonClick
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "sm:hidden",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start gap-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "relative shrink-0",
                                children: [
                                    type === "owned" && /*#__PURE__*/ _jsx(Badge, {
                                        color: "indigo",
                                        label: "보유중",
                                        type: "text",
                                        className: "absolute top-[-10px] left-[-5px]"
                                    }),
                                    /*#__PURE__*/ _jsx(Thumbnail, {
                                        size: "sm",
                                        src: imageUrl,
                                        alt: title,
                                        border: true,
                                        classes: {
                                            root: "shrink-0"
                                        }
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body1",
                                        className: "text-strong group-hover/item:text-primary-main group-has-[button:hover]/item:text-strong line-clamp-3",
                                        children: title
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-0.5",
                                        children: authorBrandUrl ? /*#__PURE__*/ _jsx(Link, {
                                            href: authorBrandUrl,
                                            className: "inline-block",
                                            onClick: stopPropagation,
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary cursor-pointer hover:underline",
                                                children: author
                                            })
                                        }) : /*#__PURE__*/ _jsx(Typography, {
                                            variant: "body2",
                                            className: "text-secondary",
                                            children: author
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1",
                                        children: /*#__PURE__*/ _jsx(PriceArea, {
                                            type: type,
                                            price: priceForDisplay,
                                            rating: rating !== null && rating !== void 0 ? rating : undefined,
                                            showCouponTag: showCouponTag,
                                            isBold: true,
                                            hasUsableEntitlement: hasUsableEntitlement
                                        })
                                    }),
                                    tags.length > 0 && /*#__PURE__*/ _jsx("div", {
                                        className: "mt-2 flex flex-wrap gap-1",
                                        children: tags.map((tag, index)=>/*#__PURE__*/ _jsx(Tag, {
                                                color: tag.color || "indigo",
                                                size: "sm",
                                                bold: tag.bold,
                                                children: tag.label
                                            }, index))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CtaAreaMobile, {
                        type: type,
                        buttons: buttons,
                        hasUsableEntitlement: hasUsableEntitlement,
                        onTrackClick: trackCtaButtonClick
                    })
                ]
            })
        ]
    });
};
/**
 * 상품 리스트 아이템 스켈레톤 컴포넌트
 * - ProductListItem과 동일한 레이아웃의 로딩 스켈레톤
 * - variant에 따라 태그/버튼 개수, 모바일 썸네일 크기 등이 달라짐
 */ export const ProductListItemSkeleton = (param)=>{
    let { className , variant ="default"  } = param;
    const isAccordion = variant === "accordion";
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("animate-pulse", className),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "hidden sm:flex sm:flex-col sm:gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start gap-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "size-[72px] shrink-0 rounded-md bg-gray-200"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "h-5 w-3/4 rounded bg-gray-200"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1 h-4 w-24 rounded bg-gray-100"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1.5 h-5 w-32 rounded bg-gray-200"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "hidden w-[200px] shrink-0 flex-col gap-1 lg:flex",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "h-6 w-16 rounded bg-gray-100"
                                    }),
                                    !isAccordion && /*#__PURE__*/ _jsx("div", {
                                        className: "h-6 w-20 rounded bg-gray-100"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "hidden w-[114px] shrink-0 flex-col gap-1 lg:flex",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "h-9 w-full rounded-md bg-gray-200"
                                    }),
                                    !isAccordion && /*#__PURE__*/ _jsx("div", {
                                        className: "h-9 w-full rounded-md bg-gray-100"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "hidden items-center justify-between sm:flex lg:hidden",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "h-6 w-16 rounded bg-gray-100"
                                    }),
                                    !isAccordion && /*#__PURE__*/ _jsx("div", {
                                        className: "h-6 w-20 rounded bg-gray-100"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    !isAccordion && /*#__PURE__*/ _jsx("div", {
                                        className: "h-9 w-20 rounded-md bg-gray-100"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "h-9 w-24 rounded-md bg-gray-200"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "sm:hidden",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: cn("flex items-start", isAccordion ? "gap-4" : "gap-3"),
                        children: [
                            isAccordion ? /*#__PURE__*/ _jsx("div", {
                                className: "h-[79px] w-[56px] shrink-0 rounded bg-gray-200"
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "size-[72px] shrink-0 rounded-md bg-gray-200"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    isAccordion ? /*#__PURE__*/ _jsx("div", {
                                        className: "h-5 w-full rounded bg-gray-200"
                                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-4 w-full rounded bg-gray-200"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "mt-1 h-4 w-3/4 rounded bg-gray-200"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: cn("h-4 w-20 rounded bg-gray-100", isAccordion ? "mt-0.5" : "mt-1")
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: cn("h-5 w-24 rounded bg-gray-200", isAccordion ? "mt-2" : "mt-1")
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-2 flex gap-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: cn("rounded bg-gray-100", isAccordion ? "h-[22px] w-10" : "h-5 w-14")
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: cn("rounded bg-gray-100", isAccordion ? "h-[22px] w-10" : "h-5 w-16")
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: cn("w-full rounded-md bg-gray-200", isAccordion ? "mt-4 h-8" : "mt-3 h-10")
                    })
                ]
            })
        ]
    });
};
